import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { JitsiSdkService } from '../services/jitsi-sdk.service';
import { InactivityService } from '../../../shared/services/inactivity.service';

/**
 * Overlay a pantalla completa que monta una videollamada Jitsi (servidor público
 * meet.jit.si) dentro de un iframe. Port a Angular del `JitsiRoom.jsx`.
 *
 * Se usa en dos contextos:
 *  - Página pública /#/meeting-room/:token (invitado, sin login).
 *  - Inline en el CRM (organizador) desde la lista / detalle de la reunión.
 *
 * El `roomName` se deriva del token de la reunión ("partnercrm-"+token), por lo
 * que organizador e invitados caen en la misma sala sin coordinar nada.
 */
@Component({
  selector: 'app-jitsi-room',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './jitsi-room.component.html',
})
export class JitsiRoomComponent implements AfterViewInit, OnDestroy {
  /** roomName que se pasa a Jitsi (= "partnercrm-"+token). Obligatorio. */
  @Input() roomName = '';
  /** Nombre a mostrar dentro de la videollamada. */
  @Input() displayName?: string;
  /** Email a mostrar dentro de la videollamada. */
  @Input() email?: string;

  // Metadata para la cabecera del overlay
  @Input() title?: string;
  @Input() datetimeStart?: string | null;
  @Input() durationMinutes?: number | null;

  /** Se emite cuando el usuario cuelga (readyToClose) o pulsa "Salir". */
  @Output() close = new EventEmitter<void>();

  @ViewChild('jitsiContainer', { static: true })
  container!: ElementRef<HTMLDivElement>;

  loading = true;
  error = '';

  private apiObj: any = null;

  /** INC 17.3: evita un doble resume si el componente se destruye dos veces. */
  private inactivitySuspended = false;

  constructor(
    private jitsiSdk: JitsiSdkService,
    private inactivity: InactivityService,
  ) {}

  ngAfterViewInit(): void {
    // INC 17.3: Jitsi renderiza en un iframe CROSS-ORIGIN (meet.jit.si), donde no
    // se pueden escuchar eventos. Mientras la sala está montada el usuario está
    // presente, así que se suspende el cierre por inactividad.
    this.inactivity.suspend();
    this.inactivitySuspended = true;
    this.startConference();
  }

  private async startConference(): Promise<void> {
    if (!this.roomName) {
      this.error = 'No se pudo determinar la sala de la reunión.';
      this.loading = false;
      return;
    }

    try {
      const JitsiMeetExternalAPI = await this.jitsiSdk.load();

      this.apiObj = new JitsiMeetExternalAPI('meet.jit.si', {
        roomName: this.roomName,
        parentNode: this.container.nativeElement,
        userInfo: {
          displayName: this.displayName || 'Invitado',
          email: this.email || '',
        },
        configOverwrite: {
          startWithAudioMuted: true,
          startWithVideoMuted: false,
          prejoinConfig: { enabled: false },
          disableDeepLinking: true,
          requireDisplayName: false,
          disableThirdPartyRequests: true,
          analytics: { disabled: true },
          lang: 'es',
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          SHOW_BRAND_WATERMARK: false,
          DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
          MOBILE_APP_PROMO: false,
          TOOLBAR_BUTTONS: [
            'microphone',
            'camera',
            'desktop',
            'chat',
            'raisehand',
            'participants-pane',
            'tileview',
            'fullscreen',
            'hangup',
          ],
        },
      });

      this.apiObj.addEventListener('readyToClose', () => {
        this.close.emit();
      });

      this.loading = false;
    } catch (e: any) {
      this.error = e?.message || 'No se pudo iniciar la videollamada.';
      this.loading = false;
    }
  }

  onClose(): void {
    this.close.emit();
  }

  formatFechaCabecera(): string {
    if (!this.datetimeStart) return '';
    try {
      const fecha = new Intl.DateTimeFormat('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(this.datetimeStart));
      return this.durationMinutes
        ? `${fecha} · ${this.durationMinutes} min`
        : fecha;
    } catch {
      return '';
    }
  }

  ngOnDestroy(): void {
    if (this.inactivitySuspended) {
      this.inactivity.resume();
      this.inactivitySuspended = false;
    }
    if (this.apiObj) {
      try {
        this.apiObj.dispose();
      } catch {
        // Ignorar errores al limpiar el iframe.
      }
      this.apiObj = null;
    }
  }
}
